import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Package, MapPin, Clock, Shield, Star, CheckCircle, 
  MessageSquare, Phone, Mail, Globe, ChevronRight, Eye, Users, Lock
} from 'lucide-react';
import { productsApi, inquiriesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { isContactLocked } from '../utils/membershipUtils';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiryData, setInquiryData] = useState({ subject: '', message: '', quantity: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const { data } = await productsApi.getById(productId);
        setProduct(data);
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Product not found');
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId, navigate]);

  const handleInquiry = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: `/products/${productId}` } } });
      return;
    }

    setSending(true);
    try {
      await inquiriesApi.create({
        product_id: productId,
        supplier_id: product.supplier_id,
        subject: inquiryData.subject || `Inquiry about ${product.title}`,
        message: inquiryData.message,
        quantity: parseInt(inquiryData.quantity) || null
      });
      toast.success('Inquiry sent successfully!');
      setInquiryOpen(false);
      setInquiryData({ subject: '', message: '', quantity: '' });
    } catch (error) {
      toast.error('Failed to send inquiry');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="container-main py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-1/4 mb-8"></div>
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="h-96 bg-slate-200 rounded-xl"></div>
              <div className="space-y-4">
                <div className="h-8 bg-slate-200 rounded w-3/4"></div>
                <div className="h-4 bg-slate-200 rounded w-full"></div>
                <div className="h-4 bg-slate-200 rounded w-2/3"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container-main py-4">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-slate-500 hover:text-slate-700">Home</Link>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <Link to="/products" className="text-slate-500 hover:text-slate-700">Products</Link>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <span className="text-slate-900 font-medium truncate">{product.title}</span>
          </nav>
        </div>
      </div>

      <div className="container-main py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Product Images */}
          <div>
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {product.images?.[0] ? (
                <img 
                  src={product.images[0]} 
                  alt={product.title}
                  className="w-full h-96 object-cover"
                />
              ) : (
                <div className="w-full h-96 flex items-center justify-center bg-slate-100">
                  <Package className="w-24 h-24 text-slate-300" />
                </div>
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="grid grid-cols-4 gap-2 mt-2">
                {product.images.slice(1, 5).map((img, i) => (
                  <div key={i} className="aspect-square bg-white border rounded-lg overflow-hidden">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Badge className="bg-blue-50 text-blue-700 border-blue-200">{product.industry}</Badge>
              {product.samples_available && (
                <Badge className="bg-green-50 text-green-700 border-green-200">Samples Available</Badge>
              )}
            </div>

            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4" data-testid="product-title">
              {product.title}
            </h1>

            <div className="flex items-center gap-4 mb-6 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {product.views} views
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {product.inquiries} inquiries
              </span>
            </div>

            {/* Price */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-6">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-3xl font-bold text-[#EA580C]">
                  ${product.price_min?.toFixed(2)} - ${product.price_max?.toFixed(2)}
                </span>
                <span className="text-slate-500">/ {product.moq_unit}</span>
              </div>
              <p className="text-sm text-slate-600">
                Minimum Order: <span className="font-semibold">{product.moq} {product.moq_unit}</span>
              </p>
            </div>

            {/* Quick Info */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-3 p-3 bg-white border rounded-lg">
                <MapPin className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Origin</p>
                  <p className="font-medium text-slate-900">{product.origin_country || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-white border rounded-lg">
                <Clock className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Lead Time</p>
                  <p className="font-medium text-slate-900">{product.lead_time || 'Contact supplier'}</p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex gap-4 mb-8">
              {isContactLocked(user) ? (
                <Button 
                  className="flex-1 bg-[#EA580C] hover:bg-[#C2410C]"
                  onClick={() => navigate('/membership')}
                  data-testid="product-inquiry-locked-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Upgrade to Contact Supplier
                </Button>
              ) : (
              <Dialog open={inquiryOpen} onOpenChange={setInquiryOpen}>
                <DialogTrigger asChild>
                  <Button className="flex-1 bg-[#EA580C] hover:bg-[#C2410C]" data-testid="product-inquiry-btn">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Contact Supplier
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Send Inquiry</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleInquiry} className="space-y-4 mt-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Subject</label>
                      <Input
                        value={inquiryData.subject}
                        onChange={(e) => setInquiryData({ ...inquiryData, subject: e.target.value })}
                        placeholder={`Inquiry about ${product.title}`}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Quantity</label>
                      <Input
                        type="number"
                        value={inquiryData.quantity}
                        onChange={(e) => setInquiryData({ ...inquiryData, quantity: e.target.value })}
                        placeholder={`Min: ${product.moq} ${product.moq_unit}`}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Message *</label>
                      <Textarea
                        value={inquiryData.message}
                        onChange={(e) => setInquiryData({ ...inquiryData, message: e.target.value })}
                        placeholder="Describe your requirements..."
                        rows={4}
                        required
                        className="mt-1"
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={sending}>
                      {sending ? 'Sending...' : 'Send Inquiry'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              )}
            </div>

            {/* Supplier Card */}
            {product.supplier && (
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#2563EB] to-[#EA580C] flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                    {product.supplier.company_name?.charAt(0) || 'S'}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Link 
                        to={`/suppliers/${product.supplier.user_id}`}
                        className="font-semibold text-slate-900 hover:text-[#2563EB]"
                      >
                        {product.supplier.company_name}
                      </Link>
                      {product.supplier.is_verified && (
                        <Badge className="badge-verified">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{product.supplier.country}</p>
                    <Link 
                      to={`/suppliers/${product.supplier.user_id}`}
                      className="text-sm text-[#2563EB] hover:underline mt-2 inline-block"
                    >
                      View Profile →
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Product Details */}
        <div className="mt-12 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Product Description</h2>
              <p className="text-slate-600 whitespace-pre-line">{product.description}</p>
            </div>

            {/* Specifications */}
            {product.specifications && (
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Specifications</h2>
                <p className="text-slate-600 whitespace-pre-line">{product.specifications}</p>
              </div>
            )}

            {/* Keywords */}
            {product.keywords?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Related Keywords</h2>
                <div className="flex flex-wrap gap-2">
                  {product.keywords.map((kw, i) => (
                    <span key={i} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Trade Info Sidebar */}
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Trade Information</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Port</span>
                  <span className="text-slate-900">{product.port || 'Contact supplier'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Packaging</span>
                  <span className="text-slate-900">{product.packaging || 'Standard'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Lead Time</span>
                  <span className="text-slate-900">{product.lead_time || 'Contact supplier'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Samples</span>
                  <span className="text-slate-900">
                    {product.samples_available ? `$${product.sample_price?.toFixed(2) || 'Available'}` : 'Contact supplier'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {product.related_products?.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Related Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {product.related_products.map((p) => (
                <Link
                  key={p.product_id}
                  to={`/products/${p.product_id}`}
                  className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="h-40 bg-slate-100">
                    {p.images?.[0] ? (
                      <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-10 h-10 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-slate-900 line-clamp-2 mb-2">{p.title}</h3>
                    <span className="text-[#EA580C] font-semibold">
                      ${p.price_min?.toFixed(2)} - ${p.price_max?.toFixed(2)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
